import { useMemo } from 'react';

/**
 * Custom hook to determine dashboard state based on user data metrics.
 * Returns stage-based flags for conditional rendering.
 *
 * Stages:
 * - empty: No stories yet
 * - first_story: Just created first story (1 story, <= 3 days active)
 * - active: Building momentum (2-5 stories, <= 7 days active)
 * - mature: Established user (5+ stories or 7+ days active)
 */
export const useDashboardState = (userData) => {
  return useMemo(() => {
    // Handle null/undefined userData
    if (!userData) {
      return {
        stage: 'empty',
        isEmpty: true,
        isNewUser: false,
        isActive: false,
        isMature: false,
        showCelebrationBanner: false,
        showFamilyEngagement: false,
        showAchievements: false,
        showWisdomPlaylists: false,
        showTimeCapsulesSection: false,
        showProgressBar: false,
        suggestedActions: ['record_first_story'],
        metrics: {
          storiesCount: 0,
          photosCount: 0,
          familyMembersCount: 0,
          timeCapsulesCount: 0,
          lifeEventsCount: 0,
          daysActive: 0,
          consecutiveDaysStreak: 0,
          totalStoryViews: 0
        }
      };
    }

    const {
      storiesCount = 0,
      photosCount = 0,
      familyMembersCount = 0,
      timeCapsulesCount = 0,
      wisdomPlaylistsCount = 0,
      lifeEventsCount = 0,
      daysActive = 0,
      consecutiveDaysStreak = 0,
      hasCompletedFirstStory = storiesCount > 0,
      totalStoryViews = 0,
      achievements = []
    } = userData;

    // Determine stage based on content and engagement metrics
    let stage = 'empty';

    if (storiesCount === 0 && lifeEventsCount === 0) {
      stage = 'empty';
    } else if (storiesCount === 1) {
      // First story - always celebrate regardless of account age
      stage = 'first_story';
    } else if (storiesCount <= 5) {
      stage = 'active';
    } else if (storiesCount > 5) {
      stage = 'mature';
    } else if (lifeEventsCount > 0) {
      // Has life events but no stories
      stage = 'active';
    }

    // Determine what sections to show
    // Show celebration banner for first story regardless of account age
    const showCelebrationBanner = hasCompletedFirstStory && storiesCount === 1;

    const showFamilyEngagement =
      familyMembersCount > 0 && stage === 'mature';

    const showAchievements =
      achievements.length > 0;

    const showWisdomPlaylists =
      wisdomPlaylistsCount > 0 || stage === 'mature';

    const showTimeCapsulesSection =
      timeCapsulesCount > 0 || stage === 'active' || stage === 'mature';

    const showProgressBar =
      stage === 'mature' && storiesCount < 20;

    // Determine suggested actions based on what's missing
    const suggestedActions = [];

    if (storiesCount === 0) {
      suggestedActions.push('record_first_story');
    } else if (storiesCount < 3) {
      suggestedActions.push('record_another_story');
    }

    if (storiesCount > 0 && photosCount === 0) {
      suggestedActions.push('upload_photos');
    }

    if (storiesCount > 0 && familyMembersCount === 0) {
      suggestedActions.push('add_family');
    }

    if (storiesCount >= 2 && wisdomPlaylistsCount === 0) {
      suggestedActions.push('create_playlist');
    }

    if (storiesCount >= 1 && timeCapsulesCount === 0) {
      suggestedActions.push('create_time_capsule');
    }

    return {
      stage,
      showCelebrationBanner,
      showFamilyEngagement,
      showAchievements,
      showWisdomPlaylists,
      showTimeCapsulesSection,
      showProgressBar,
      suggestedActions,

      // Helper flags
      isEmpty: stage === 'empty',
      isNewUser: stage === 'first_story',
      isActive: stage === 'active',
      isMature: stage === 'mature',

      // Metrics for display
      metrics: {
        storiesCount,
        photosCount,
        familyMembersCount,
        timeCapsulesCount,
        lifeEventsCount,
        daysActive,
        consecutiveDaysStreak,
        totalStoryViews
      }
    };
  }, [userData]);
};

export default useDashboardState;
